import { Component, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
} from '@angular/forms';
import { ChangelogService } from '../../services/changelog/changelog.service';
import type {
  ChangelogCategory,
  ChangelogEntry,
} from '../../models/changelog/changelog.model';
import { Router } from '@angular/router';

@Component({
  selector: 'app-changelog-create',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './changelog-create.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./changelog-create.component.scss'],
})
export class ChangelogCreateComponent {
  changelogForm: FormGroup;
  categories: { value: ChangelogCategory; label: string }[] = [
    { value: 'feature', label: 'Ny funktion' },
    { value: 'improvement', label: 'Forbedring' },
    { value: 'bugfix', label: 'Fejlrettelse' },
  ];
  isSubmitting = false;
  submitSuccess = false;
  submitError = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private changelogService: ChangelogService,
    private router: Router
  ) {
    this.changelogForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(100)]],
      date: [this.formatDate(new Date()), Validators.required],
      description: ['', [Validators.required, Validators.maxLength(500)]],
      categories: [[], Validators.required],
      details: this.fb.array([this.createDetailControl()]),
    });
  }

  get detailsFormArray(): FormArray {
    return this.changelogForm.get('details') as FormArray;
  }

  createDetailControl() {
    return this.fb.control('', [Validators.required]);
  }

  addDetailField() {
    this.detailsFormArray.push(this.createDetailControl());
  }

  removeDetailField(index: number) {
    if (this.detailsFormArray.length > 1) {
      this.detailsFormArray.removeAt(index);
    }
  }

  toggleCategory(category: ChangelogCategory) {
    const currentCategories = [
      ...this.changelogForm.get('categories')?.value,
    ] as ChangelogCategory[];

    if (currentCategories.includes(category)) {
      this.changelogForm
        .get('categories')
        ?.setValue(currentCategories.filter((cat) => cat !== category));
    } else {
      this.changelogForm
        .get('categories')
        ?.setValue([...currentCategories, category]);
    }
  }

  isCategorySelected(category: ChangelogCategory): boolean {
    const currentCategories = this.changelogForm.get('categories')
      ?.value as ChangelogCategory[];
    return currentCategories.includes(category);
  }

  onSubmit() {
    if (this.changelogForm.invalid) {
      this.markFormGroupTouched(this.changelogForm);
      return;
    }

    this.isSubmitting = true;
    this.submitSuccess = false;
    this.submitError = false;

    const formValue = this.changelogForm.value;

    const changelogEntry: Omit<ChangelogEntry, 'id'> = {
      title: formValue.title,
      date: new Date(formValue.date),
      categories: formValue.categories,
      description: formValue.description,
      details: formValue.details.filter(
        (detail: string) => detail.trim() !== ''
      ),
    };

    this.changelogService.createChangelogEntry(changelogEntry).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.resetForm();
        // Navigate to the changelog list after successful creation
        setTimeout(() => {
          this.router.navigate(['/changelog']);
        }, 2000);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.submitError = true;
        this.errorMessage =
          error.error?.message || 'Kunne ikke oprette changelog-indlæg';
        console.error('Error creating changelog entry:', error);
      },
    });
  }

  resetForm() {
    this.changelogForm.reset({
      title: '',
      date: this.formatDate(new Date()),
      description: '',
      categories: [],
    });

    // Reset details array to have just one empty field
    this.detailsFormArray.clear();
    this.addDetailField();
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((c) => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          } else {
            c.markAsTouched();
          }
        });
      }
    });
  }
}
